import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type PropsWithPartialAnchor, useExternalAnchor} from '@github-ui/react-core/react-partial-anchor'
import {GlobalCommands} from '@github-ui/ui-commands'
import {
  CodespacesIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  GiftIcon,
  GitPullRequestIcon,
  GlobeIcon,
  HomeIcon,
  IssueOpenedIcon,
  McpIcon,
  RepoIcon,
  SparkleIcon,
  TableIcon,
  TelescopeIcon,
} from '@primer/octicons-react'
import {type DialogProps, NavList} from '@primer/react'
import {useCallback} from 'react'

import type {GlobalNavigationCommandId} from '../types'
import {GlobalNavAnalyticsProvider, useGlobalNavAnalytics} from '../utils/Analytics'
import {analyticsCategory} from './constants'
import {DynamicSidePanelContent} from './dynamic-content/DynamicSidePanelContent'
import {SideNavMenu, SideNavMenuItem, type SideNavMenuItemProps} from './SideNavMenu'

type ConfigKeys = (typeof GlobalMenuItems)[number][number]['key']

export type WithShow<T> = T & {
  /** Enables overriding the default to hide/show the menu-item on selective configurations */
  show?: boolean
}

type NavMenuOverrides = Partial<Record<ConfigKeys, Partial<WithShow<Pick<GlobalNavMenuItem, 'href'>>>>>

export type GlobalNavMenuProps = NavMenuOverrides & {
  /**
   * If the menu is currently open or not
   * Even when the menu is closed, this is required to support user hot-keys for menu items behind the menu.
   * @default true
   */
  open?: boolean

  richContent?: {
    /**
     * Enables the display of rich content in the navigation menu.
     * This is used to display dynamic content such as repositories.
     *
     * @default false
     */
    show?: boolean

    /**
     * The location to pull the initial config for rich content from.
     * These endpoints should return the initial data required to populate repositories.
     * If `richContentEnabled` is false or not provided, these URLs will be ignored.
     */
    contentUrl?: string

    repositoriesSearchUrl?: string
  }
}

/**
 * The main navigation menu component for the application.
 * It renders a side navigation menu with support for rich content.
 *
 * Handling for specific menu-items can be controlled via the individual props:
 * i.e.
 * ```tsx
 * <GlobalNavMenu
 *   home={{ show: true, href: '/custom-home' }}
 *   issues={{ show: false }}
 * />
 * ```
 *
 */
export function GlobalNavMenu(props: GlobalNavMenuProps & Pick<DialogProps, 'onClose' | 'returnFocusRef'>) {
  const {open = true, onClose, richContent} = props

  const [mainGroup, secondaryGroup] = GlobalMenuItems.map(group => {
    return group
      .filter(item => {
        if (props[item.key]?.show !== undefined) {
          return props[item.key]?.show
        }

        return item.show !== false
      })
      .map(({show: _show, ...item}) => {
        const {show, ...overrides} = props[item.key] ?? {}

        // Conditionally set copilot href and analytics key based on feature flags
        const copilotExperimentOverride = {}
        if (item.key === 'copilot') {
          let analyticsKey = item.analyticsKey
          if (isFeatureEnabled('copilot_default_to_task_input')) {
            analyticsKey += '_TASK'
            Object.assign(copilotExperimentOverride, {
              href: '/copilot/agents',
            })
          }
          if (isFeatureEnabled('copilot_default_to_task_input_control_group')) {
            analyticsKey += '_TASK_EXPERIMENT_CONTROL'
          }
          Object.assign(copilotExperimentOverride, {
            analyticsKey,
          })
        }

        return {
          ...item,
          ...overrides,
          ...copilotExperimentOverride,
        }
      })
  })

  return (
    <GlobalNavAnalyticsProvider category={analyticsCategory}>
      <GlobalNavCommands menuItems={[...(mainGroup ?? []), ...(secondaryGroup ?? [])]} />

      {open && (
        <SideNavMenu onClose={onClose}>
          <NavList.Group>
            {mainGroup?.map(({key, ...item}) => {
              return <SideNavMenuItem key={key} {...item} />
            })}
          </NavList.Group>

          {(secondaryGroup?.length ?? 0) > 0 && (
            <NavList.Group>
              {secondaryGroup?.map(({key, ...item}) => {
                return <SideNavMenuItem key={key} {...item} />
              })}
            </NavList.Group>
          )}
          {richContent?.show && richContent.contentUrl && (
            <DynamicSidePanelContent
              richContentUrl={richContent.contentUrl}
              repositoriesSearchUrl={richContent.repositoriesSearchUrl}
            />
          )}
        </SideNavMenu>
      )}
    </GlobalNavAnalyticsProvider>
  )
}

/**
 * Render the global UI commands provider for menu items (rendered even when the menu is closed).
 */
export function GlobalNavCommands({menuItems}: {menuItems: GlobalNavMenuItem[]}) {
  const {sendItemClick} = useGlobalNavAnalytics()

  const commandEntries = menuItems
    .filter((item): item is GlobalNavMenuItem & {commandId: GlobalNavigationCommandId} => item.commandId !== undefined)
    .map(
      ({commandId, href, analyticsKey}) =>
        [
          commandId,
          () => {
            window.location.assign(href)
            if (analyticsKey) {
              sendItemClick(analyticsKey)
            }
          },
        ] as const,
    )

  return <GlobalCommands commands={Object.fromEntries(commandEntries)} />
}

export function GlobalNavMenuPartial({reactPartialAnchor, ...props}: PropsWithPartialAnchor<GlobalNavMenuProps>) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(reactPartialAnchor)

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return <GlobalNavMenu returnFocusRef={anchorRef} open={open} onClose={onClose} {...props} />
}

export type GlobalNavMenuItem = SideNavMenuItemProps & {
  /** The config key to use when overriding props */
  key: string
  /** Optional UI Command ID to trigger the menu item */
  commandId?: GlobalNavigationCommandId
}

export const GlobalMenuItems = [
  [
    {
      href: '/dashboard',
      key: 'home',
      icon: HomeIcon,
      label: 'Home',
      analyticsKey: 'HOME',
      show: true,
      activePath: {
        additionalPathnames: ['/'],
      },
    },
    {
      href: '/feed',
      key: 'feed',
      icon: GlobeIcon,
      label: 'Feed',
      analyticsKey: 'FEED',
      show: false,
    },
    {
      href: '/issues',
      key: 'issues',
      icon: IssueOpenedIcon,
      label: 'All issues',
      analyticsKey: 'ISSUES',
      show: true,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/pulls',
      key: 'pulls',
      icon: GitPullRequestIcon,
      label: 'All pull requests',
      analyticsKey: 'PULL_REQUESTS',
      show: true,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/repos',
      key: 'contributedRepos',
      label: 'All repositories',
      analyticsKey: 'CONTRIBUTED_REPOS',
      icon: RepoIcon,
      show: false,
    },
    {
      href: '/projects',
      key: 'projects',
      icon: TableIcon,
      label: 'Projects',
      analyticsKey: 'PROJECTS',
      show: true,
    },
    {
      href: '/discussions',
      key: 'discussions',
      icon: CommentDiscussionIcon,
      label: 'Discussions',
      analyticsKey: 'DISCUSSIONS',
      show: false,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/codespaces',
      key: 'codespaces',
      icon: CodespacesIcon,
      label: 'Codespaces',
      analyticsKey: 'CODESPACES',
      show: false,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/copilot',
      key: 'copilot',
      icon: CopilotIcon,
      label: 'Copilot',
      analyticsKey: 'COPILOT',
      show: false,
    },
    {
      href: '/spark',
      key: 'spark',
      icon: SparkleIcon,
      label: 'Spark',
      analyticsKey: 'SPARK',
      show: false,
    },
  ],
  [
    {
      href: '/explore',
      key: 'explore',
      icon: TelescopeIcon,
      label: 'Explore',
      analyticsKey: 'EXPLORE',
      show: false,
    },
    {
      href: '/marketplace',
      key: 'marketplace',
      icon: GiftIcon,
      label: 'Marketplace',
      analyticsKey: 'MARKETPLACE',
      show: false,
    },
    {
      href: '/mcp',
      key: 'mcp',
      icon: McpIcon,
      label: 'MCP registry',
      analyticsKey: 'MCP_REGISTRY',
      show: false,
      activePath: {
        includeSubpaths: true,
      },
    },
  ],
  // The `as const` lets us pull out the `key` properties in TS dynamically
  // The `satisfies` lets us still type-check this object against the GlobalNavMenuItem structure
] as const satisfies Array<Array<WithShow<GlobalNavMenuItem>>>
